import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { PurchaseOrderEnums } from 'src/app/enum/purchase-order-enum';
import { PurchaseOrderDetailModel } from 'src/app/models/shopping/purchase-order-detail.model';
import { PurchaseOrderModel } from 'src/app/models/shopping/purchase-order.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { PurchaseOrderDetailService } from 'src/app/services/shopping/purchase-order-detail.service';

@Component({
  selector: 'app-register-purchase-order-detail',
  templateUrl: './register-purchase-order-detail.component.html',
  styleUrls: ['./register-purchase-order-detail.component.scss'],
  standalone: false
})
export class RegisterPurchaseOrderDetailComponent implements OnInit {

  @Input() purchaseOrderDetailModel: PurchaseOrderDetailModel = new PurchaseOrderDetailModel();
  @Input() purchaseOrderModel: PurchaseOrderModel = new PurchaseOrderModel();
  @Output() updateListDetail = new EventEmitter<boolean>();
  @Output() isFormView = new EventEmitter<boolean>();

  codeNameArticle: string = '';
  formPurchaseOrderDetail: FormGroup = new FormGroup({});
  hiddenAjusted: boolean = true;
  hiddenNoAjusted: boolean = false;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private purchaseOrderDetailService: PurchaseOrderDetailService,
    private toastr: ToastrService) {

  }

  ngOnInit() {
    this.validateForm(new PurchaseOrderDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    let purchaseOrderDetailModel = changes["purchaseOrderDetailModel"];
    if (purchaseOrderDetailModel && !purchaseOrderDetailModel.firstChange) {
      this.validateForm(this.purchaseOrderDetailModel);
    }
  }

  calculateTotals() {
    let ajustedUnitValue = this.formPurchaseOrderDetail.get("ajustedUnitValue")?.value;
    let ajustedQuantity = this.formPurchaseOrderDetail.get("ajustedQuantity")?.value;
    let ajustedDiscountRate = this.formPurchaseOrderDetail.get("ajustedDiscountRate")?.value;
    this.formPurchaseOrderDetail.get("subtotalValue")?.setValue(ajustedUnitValue * ajustedQuantity * (1 - ajustedDiscountRate / 100));
  }

  changeDiscountRate() {
    let discountRate = this.formPurchaseOrderDetail.get("discountRate")?.value;
    this.formPurchaseOrderDetail.controls["ajustedDiscountRate"].setValue(discountRate);
    this.calculateTotals();
  }

  changeIvaRate() {
    let ivaRate = this.formPurchaseOrderDetail.get("ivaRate")?.value;
    this.formPurchaseOrderDetail.controls["ajustedIvaRate"].setValue(ivaRate);
  }

  changeQuantity() {
    let quantity = this.formPurchaseOrderDetail.get("quantity")?.value;
    this.formPurchaseOrderDetail.controls["ajustedQuantity"].setValue(quantity);
    this.calculateTotals();
  }

  changeUnitValue() {
    let unitValue = this.formPurchaseOrderDetail.get("unitValue")?.value;
    this.formPurchaseOrderDetail.controls["ajustedUnitValue"].setValue(unitValue);
    this.calculateTotals();
  }

  clearForm() {
    this.codeNameArticle = "";
    this.validateForm(new PurchaseOrderDetailModel);
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null) {
    try {
      if (articleId != null && articleId != 0) {
        let article = await firstValueFrom(this.articleService.getArticleById(articleId));
        if (article) {
          this.codeNameArticle = article.code + " - " + article.name;
        } else {
          this.codeNameArticle = "Error al buscar el elemento. Por favor refresque la página.";
        }
      } else {
        this.codeNameArticle = "";
      }
    } catch (error) {
      console.error('Error al obtener el artículo:', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView.emit(isFormView);
  }

  async send() {
    if (this.formPurchaseOrderDetail.valid) {
      let modelPurchaseOrderDetail = this.formPurchaseOrderDetail.value as PurchaseOrderDetailModel;
      this.purchaseOrderDetailModel.quantity = modelPurchaseOrderDetail.quantity;
      this.purchaseOrderDetailModel.ajustedQuantity = modelPurchaseOrderDetail.ajustedQuantity;
      this.purchaseOrderDetailModel.unitValue = modelPurchaseOrderDetail.unitValue;
      this.purchaseOrderDetailModel.ajustedUnitValue = modelPurchaseOrderDetail.ajustedUnitValue;
      this.purchaseOrderDetailModel.discountRate = modelPurchaseOrderDetail.discountRate;
      this.purchaseOrderDetailModel.ajustedDiscountRate = modelPurchaseOrderDetail.ajustedDiscountRate;
      this.purchaseOrderDetailModel.ivaRate = modelPurchaseOrderDetail.ivaRate;
      this.purchaseOrderDetailModel.ajustedIvaRate = modelPurchaseOrderDetail.ajustedIvaRate;
      try {
        await firstValueFrom(this.purchaseOrderDetailService.updatePurchaseOrderDetail(this.purchaseOrderDetailModel));
        this.toastr.success('El detalle de la orden de compra fue modificado exitosamente.', '', {
          timeOut: 5000, progressBar: true, closeButton: true
        });
        this.purchaseOrderDetailModel = new PurchaseOrderDetailModel();
        this.clearForm();
        this.isFormView.emit(false);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al actualizar el detalle del pedido interno.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    let controlMessages: any = {
      quantity: 'El campo cantidad es obligatorio.',
      ajustedQuantity: 'El campo cantidad ajustada es obligatorio.',
      unitValue: 'El campo valor unitario es obligatorio.',
      ajustedUnitValue: 'El campo valor unitario ajustado es obligatorio.',
      discountRate: 'El campo % descuento es obligatorio.',
      ajustedDiscountRate: 'El campo % descuento ajustado es obligatorio.',
      ivaRate: 'El campo % iva es obligatorio.',
      ajustedIvaRate: 'El campo % iva ajustado es obligatorio.'
    };
    Object.keys(controlMessages).forEach(control => {
      if (this.formPurchaseOrderDetail.controls[control].invalid) {
        this.toastr.warning(controlMessages[control], 'Atención', {
          timeOut: 5000, progressBar: true, closeButton: true
        });
      }
    });
  }

  updateStateForm() {
    if (this.purchaseOrderModel.purchaseOrderState == PurchaseOrderEnums.stateOpen ||
      this.purchaseOrderModel.purchaseOrderState == PurchaseOrderEnums.stateInitialClose) {
      setTimeout(() => this.formPurchaseOrderDetail.enable(), 100);
      setTimeout(() => this.formPurchaseOrderDetail.controls["subtotalValue"].disable(), 100);
      if (this.purchaseOrderModel.purchaseOrderState == PurchaseOrderEnums.stateOpen) {
        this.hiddenAjusted = true;
        this.hiddenNoAjusted = false;
      } else {
        this.hiddenAjusted = false;
        this.hiddenNoAjusted = true;
      }
    } else {
      setTimeout(() => this.formPurchaseOrderDetail.disable(), 100);
    }
  }

  async validateForm(purchaseOrderDetailModel: PurchaseOrderDetailModel) {
    this.formPurchaseOrderDetail = this.formBuilder.group({
      quantity: [purchaseOrderDetailModel.quantity, [Validators.required]],
      ajustedQuantity: [purchaseOrderDetailModel.ajustedQuantity, [Validators.required]],
      unitValue: [purchaseOrderDetailModel.unitValue, [Validators.required]],
      ajustedUnitValue: [purchaseOrderDetailModel.ajustedUnitValue, [Validators.required]],
      discountRate: [purchaseOrderDetailModel.discountRate, [Validators.required]],
      ajustedDiscountRate: [purchaseOrderDetailModel.ajustedDiscountRate, [Validators.required]],
      subtotalValue: [purchaseOrderDetailModel.ajustedUnitValue * purchaseOrderDetailModel.ajustedQuantity *
        (1 - purchaseOrderDetailModel.ajustedDiscountRate / 100)],
      ivaRate: [purchaseOrderDetailModel.ivaRate, [Validators.required]],
      ajustedIvaRate: [purchaseOrderDetailModel.ajustedIvaRate, [Validators.required]]
    });
    await this.getArticle(purchaseOrderDetailModel.articleId);
    this.updateStateForm();
  }
}
